import React, { useState, useEffect } from 'react';
import { dataService } from '../services/dataService';
import SummaryRenderer from './steps/SummaryRenderer';
import { enrichPermitWithLinks } from '../utils/linkEnricher';
import PermitList from './common/PermitList';

export default function ApplicantDashboard({ currentUser, onBack, onHome, onEdit }) {
    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPermit, setSelectedPermit] = useState(null);

    const [activeTab, setActiveTab] = useState('inbox'); // 'inbox' (Lopend) or 'archive' (Historie)

    useEffect(() => {
        loadPermits();
    }, [currentUser]);

    // New Effect: Enrich selected permit with hierarchy data (links)
    useEffect(() => {
        const enrichSelected = async () => {
            if (!selectedPermit || !selectedPermit.location) return;

            // Optimistically show what we have, but fetch detailed hierarchy
            try {
                // We need the full location hierarchy to find Subline/Line links
                const locData = await dataService.getLocation(selectedPermit.location);
                if (locData && locData.hierarchy) {
                    const enriched = enrichPermitWithLinks(selectedPermit, locData.hierarchy);
                    // Only update if it actually added something or changed
                    setSelectedPermit(prev => ({ ...prev, ...enriched }));
                }
            } catch (error) {
                console.error("Error enriching permit:", error);
            }
        };

        if (selectedPermit && !selectedPermit._enriched) {
            enrichSelected();
        }
    }, [selectedPermit]);

    const loadPermits = async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const data = await dataService.getPermitsByApplicant(currentUser.uid);
            // Sort by createdAt desc
            data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setPermits(data);
        } catch (error) {
            console.error("Error loading permits:", error);
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic
    const filteredPermits = permits.filter(p => {
        if (activeTab === 'inbox') {
            return ['pending', 'in_progress', 'assigned'].includes(p.status); // Active
        } else {
            return ['completed', 'approved', 'rejected'].includes(p.status); // Archive
        }
    });

    return (
        <div className="min-h-screen bg-slate-900 text-slate-200 p-4 md:p-8">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8 gap-4">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button onClick={onBack} className="btn btn-sm btn-secondary text-white shadow-md">
                                ⬅️ Terug
                            </button>
                        )}
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-white">Mijn Aanvragen/Toewijzingen</h1>
                            <p className="text-slate-400">Overzicht van jouw ingediende werkplannen</p>
                        </div>
                    </div>
                    {onHome && (
                        <button onClick={onHome} className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-slate-200 border-slate-600">
                            🏠 Home
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* List */}
                    <div className="lg:col-span-1 space-y-4">

                        {/* Tabs */}
                        <div className="flex gap-2 p-1 bg-slate-800 rounded-lg">
                            <button
                                onClick={() => { setActiveTab('inbox'); setSelectedPermit(null); }}
                                className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'inbox' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                                Lopend
                            </button>
                            <button
                                onClick={() => { setActiveTab('archive'); setSelectedPermit(null); }}
                                className={`flex-1 py-2 text-sm font-bold rounded-md transition-all ${activeTab === 'archive' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                                Archief
                            </button>
                        </div>

                        <div className="card bg-slate-800 border-slate-700 p-4 min-h-[500px]">
                            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                                {activeTab === 'inbox' ? 'Lopende Aanvragen' : 'Geschiedenis'}
                                <span className="bg-slate-700 text-white text-xs px-2 py-1 rounded-full">{filteredPermits.length}</span>
                            </h2>
                            <PermitList
                                permits={filteredPermits}
                                loading={loading}
                                selectedPermit={selectedPermit}
                                onSelect={(permit) => setSelectedPermit(selectedPermit?.id === permit.id ? null : permit)}
                                showApplicant={false}
                                emptyMessage={activeTab === 'inbox' ? "Geen lopende aanvragen." : "Geen items in archief."}
                            />
                        </div>
                    </div>

                    {/* Details */}
                    <div className="lg:col-span-2 relative">
                        {selectedPermit ? (
                            <div className="relative">
                                {/* Close Button - Moved outside animation container for stable positioning */}
                                <button
                                    onClick={() => setSelectedPermit(null)}
                                    style={{ position: 'absolute', top: 0, right: 0, zIndex: 50 }}
                                    className="btn btn-circle btn-sm btn-ghost bg-slate-800 text-slate-400 hover:text-white border border-slate-600 shadow-lg"
                                    title="Sluiten"
                                >
                                    ✕
                                </button>
                                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300 pt-8">
                                    {/* Feedback Card if Rejected */}
                                    {/* Feedback Card if Rejected */}
                                    {selectedPermit.status === 'rejected' && (
                                        <div className="card bg-red-900/20 border-red-500/50 p-4 border flex justify-between items-start">
                                            <div>
                                                <h3 className="text-red-400 font-bold mb-1">⚠️ Afgekeurd</h3>
                                                <p className="text-slate-300 text-sm">
                                                    <span className="font-bold">Reden:</span> {selectedPermit.feedback}
                                                </p>
                                                <div className="text-slate-500 text-xs mt-2 space-y-1">
                                                    <p>Beoordeeld door: {selectedPermit.reviewer}</p>
                                                    <p>📅 Aangevraagd: {new Date(selectedPermit.createdAt).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                    <p>⚠️ Afgekeurd: {new Date(selectedPermit.rejectedAt || selectedPermit.reviewedAt).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                </div>
                                            </div>
                                            {onEdit && (
                                                <button
                                                    onClick={() => onEdit(selectedPermit)}
                                                    className="btn bg-red-600 hover:bg-red-500 text-white text-sm px-4 py-2 rounded-lg shadow-lg"
                                                >
                                                    ✏️ Bewerk & Opnieuw Indienen
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Approval Info */}
                                    {selectedPermit.status === 'approved' && (
                                        <div className="card bg-green-900/20 border-green-500/50 p-4 border">
                                            <h3 className="text-green-400 font-bold mb-1">✅ Goedgekeurd</h3>
                                            <div className="text-slate-500 text-xs mt-2 space-y-1">
                                                <p>Goedgekeurd door: {selectedPermit.reviewer}</p>
                                                <p>📅 Aangevraagd: {new Date(selectedPermit.createdAt).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                                <p>✅ Goedgekeurd: {new Date(selectedPermit.approvedAt || selectedPermit.reviewedAt).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="card bg-slate-800/50 p-8 shadow-2xl border border-slate-700">
                                        <SummaryRenderer
                                            data={selectedPermit}
                                            darkMode={true}
                                            config={selectedPermit.template_config || {
                                                spacing: 'normal',
                                                sections: [
                                                    { id: 'opdracht', label: 'Opdracht', visible: true },
                                                    { id: 'locatie', label: 'Locatie & Object', visible: true },
                                                    { id: 'taak', label: 'Taak & Kwalificatie', visible: true },
                                                    { id: 'personeel', label: 'Personeel', visible: true },
                                                    { id: 'pbms', label: "Benodigde PBM's", visible: true },
                                                    { id: 'gereedschap', label: 'Gereedschap', visible: true },
                                                    { id: 'vergunningen', label: 'Vereiste Werkplannen', visible: true },
                                                    { id: 'loto', label: 'LOTO Procedures', visible: true },
                                                    { id: 'risicos', label: "Risico's & Maatregelen", visible: true },
                                                    { id: 'werkstappen', label: 'Werkstappen', visible: true },
                                                    { id: 'images', label: 'Foto\'s', visible: true },
                                                    { id: 'handtekeningen', label: 'Handtekeningen', visible: true }
                                                ]
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex items-center justify-center text-slate-500 border-2 border-dashed border-slate-800 rounded-lg min-h-[400px]">
                                Selecteer een aanvraag om details te zien
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
